import { useMemo } from 'react'
import type { MetadataFieldDescriptor } from '../../MetadataFields.js'
import Select, { components, type MultiValue, type OptionProps, type SingleValue } from 'react-select'
import { CheckIcon } from '@heroicons/react/24/outline'
import type { VisualizationSampleContainer } from '../../VisualizationSampleContainer.js'

type Props = {
  samples: VisualizationSampleContainer[]
  field: MetadataFieldDescriptor
  multiselect: boolean
  value: string[]
  onChange: (values: string[]) => void
}

type Option = {
  label: string
  value: string
}

/**
 * react-select gives the focused row a background too, so a selected row's own background does not
 * distinguish the two. The tick says which values are in the list.
 */
const OptionWithTick = (props: OptionProps<Option, boolean>) => (
  <components.Option {...props}>
    <span className="flex items-center gap-2">
      <CheckIcon className={`w-4 h-4 shrink-0 ${props.isSelected ? '' : 'invisible'}`} />
      <span className="grow">{props.label}</span>
    </span>
  </components.Option>
)

export function ValueListSelector({ samples, field, multiselect, value, onChange }: Props) {
  const options: Option[] = useMemo(() => {
    const rawValues = samples
      .map((x) => ({
        label: field.uiLabel ? field.uiLabel(x) : undefined,
        value: field.comparisonKey ? field.comparisonKey(x) : field.getter(x)
      }))
      .filter((v) => v.value !== undefined && (typeof v.value === 'string' || typeof v.value === 'number' || typeof v.value === 'boolean'))
      .map((v) => ({
        value: String(v.value),
        label: String(v.label ? v.label : v.value)
      }))

    const valueSet = new Set<string>()
    const res: { value: string; label: string }[] = []

    rawValues.forEach((entry) => {
      if (!valueSet.has(entry.value)) {
        valueSet.add(entry.value)
        res.push(entry)
      }
    })

    return res.sort((a, b) => a.label.localeCompare(b.label))
  }, [samples, field])

  const selectedOptions = options.filter((opt) => value.includes(opt.value))

  function isMultiselect(selected: MultiValue<Option> | SingleValue<Option> | null): selected is MultiValue<Option> {
    return selected !== null && Array.isArray(selected)
  }

  function isSingleSelect(selected: MultiValue<Option> | SingleValue<Option> | null): selected is SingleValue<Option> {
    return selected !== null && !Array.isArray(selected)
  }

  const handleChange = (selected: MultiValue<Option> | SingleValue<Option> | null) => {
    if (selected === null) {
      onChange([])
    } else if (multiselect) {
      if (!isMultiselect(selected)) throw Error('Must be multiselect')
      onChange(selected.map((x) => x.value))
    } else {
      if (!isSingleSelect(selected)) throw Error('Must be single select')
      onChange([selected.value])
    }
  }

  return (
    <Select<Option, boolean>
      options={options}
      isMulti={multiselect}
      value={multiselect ? selectedOptions : (selectedOptions[0] ?? null)}
      onChange={handleChange}
      /* picking a value used to shut the menu, so building a list of ten meant twenty clicks */
      closeMenuOnSelect={!multiselect}
      /* react-select removes chosen options from a multi menu by default, which is why the menu showed
         no sign of what was already picked and offered no way to take one back out again */
      hideSelectedOptions={false}
      components={multiselect ? { Option: OptionWithTick } : undefined}
      menuPlacement="auto"
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menu: (base) => ({ ...base, zIndex: 1000 })
      }}
      className="w-full text-sm"
      classNamePrefix="select"
    />
  )
}
